import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {ErrorBoundary, FallbackProps} from 'react-error-boundary'
import TableHistoryAg from '../modules/widgets/components/TableHistoryAg'
import {JobDetailsPanel} from '../modules/widgets/components/JobDetailsPanel'
import {JobLogsPanel, clearLogsState} from '../modules/widgets/components/JobLogsPanel'
import type {LogTabType} from '../modules/widgets/components/JobLogsPanel'

type PanelType = 'details' | 'logs'

const PANEL_STATE_KEY = 'jobs-panel-state'

interface PersistedPanelState {
  detailsJob: any | null
  logsJob: any | null
  logsTabType: LogTabType
  panelOrder: PanelType[]
}

function loadPanelState(): PersistedPanelState | null {
  try {
    const raw = sessionStorage.getItem(PANEL_STATE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore corrupt data */ }
  return null
}

function savePanelState(state: PersistedPanelState) {
  try {
    sessionStorage.setItem(PANEL_STATE_KEY, JSON.stringify(state))
  } catch { /* storage full or unavailable */ }
}

function ErrorFallback({error, resetErrorBoundary}: FallbackProps) {
  return (
    <div role='alert' className='alert alert-danger'>
      <h4>Something went wrong:</h4>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary} className='btn btn-primary me-2'>
        Try again
      </button>
      <button onClick={() => (window.location.href = '/jobs')} className='btn btn-secondary'>
        Go back to jobs
      </button>
    </div>
  )
}

interface PanelTitleProps {
  title: string
  subtitle?: string
  isFirst: boolean
}

const PanelTitle: FC<PanelTitleProps> = ({title, subtitle, isFirst}) => (
  <div
    className={isFirst ? 'card-header border-0 pt-5' : ''}
    style={{
      minHeight: isFirst ? '110px' : undefined,
      flexShrink: 0,
      paddingTop: isFirst ? undefined : 'var(--ob-spacing-2, 0.5rem)',
      paddingBottom: 'var(--ob-spacing-2, 0.5rem)',
    }}
  >
    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
      <h3>{title}</h3>
      {subtitle && (
        <h5 style={{color: 'grey', alignContent: 'center'}}>{subtitle}</h5>
      )}
    </div>
  </div>
)

interface PanelCloseButtonProps {
  label: string
  onClose: () => void
}

const PanelCloseButton: FC<PanelCloseButtonProps> = ({label, onClose}) => (
  <button
    className='btn btn-sm btn-icon'
    onClick={onClose}
    title={`Close ${label}`}
    style={{
      position: 'absolute',
      top: '8px',
      right: '8px',
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
    }}
  >
    <i className='fa-solid fa-xmark' style={{fontSize: 'var(--ob-font-size-lg)'}}></i>
  </button>
)

const JobViewerPage: FC = () => {
  const persisted = loadPanelState()
  const [detailsJob, setDetailsJob] = useState<any>(persisted?.detailsJob ?? null)
  const [logsJob, setLogsJob] = useState<any>(persisted?.logsJob ?? null)
  const [logsTabType, setLogsTabType] = useState<LogTabType>(persisted?.logsTabType ?? 'logs')
  const [panelOrder, setPanelOrder] = useState<PanelType[]>(persisted?.panelOrder ?? [])
  const [scrollTarget, setScrollTarget] = useState<PanelType | null>(null)
  const detailsPanelRef = useRef<HTMLDivElement>(null)
  const logsPanelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    savePanelState({detailsJob, logsJob, logsTabType, panelOrder})
  }, [detailsJob, logsJob, logsTabType, panelOrder])

  useEffect(() => {
    if (!scrollTarget) return
    const ref = scrollTarget === 'details' ? detailsPanelRef : logsPanelRef
    const frameId = requestAnimationFrame(() => {
      ref.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
    })
    setScrollTarget(null)
    return () => cancelAnimationFrame(frameId)
  }, [scrollTarget])

  const handleViewDetails = useCallback((job: any) => {
    setDetailsJob(job)
    setPanelOrder((prev) => (prev.includes('details') ? prev : [...prev, 'details']))
    setScrollTarget('details')
  }, [])

  const openLogsPanel = useCallback((job: any, tabType: LogTabType) => {
    setLogsJob(job)
    setLogsTabType(tabType)
    setPanelOrder((prev) => (prev.includes('logs') ? prev : [...prev, 'logs']))
    setScrollTarget('logs')
  }, [])

  const handleViewLogs = useCallback((job: any) => {
    openLogsPanel(job, 'logs')
  }, [openLogsPanel])

  const handleViewMessages = useCallback((job: any) => {
    openLogsPanel(job, 'messages')
  }, [openLogsPanel])

  const handleCloseDetails = useCallback(() => {
    setDetailsJob(null)
    setPanelOrder((prev) => prev.filter((p) => p !== 'details'))
  }, [])

  const handleCloseLogs = useCallback(() => {
    setLogsJob(null)
    setPanelOrder((prev) => prev.filter((p) => p !== 'logs'))
    clearLogsState()
  }, [])

  const hasRightColumn = detailsJob || logsJob

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div
        className='ob-page'
        style={{
          paddingTop: 'var(--ob-spacing-6)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* Content: Analysis column + optional right panel column */}
        <div
          className='ob-jobs__content-row'
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Analysis column */}
          <div style={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0}}>
            <div className='card-header border-0 pt-5' style={{minHeight: '110px', flexShrink: 0}}>
              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
                <h3>Analysis</h3>
                <h5 style={{color: 'grey', alignContent: 'center'}}>
                  Monitor and manage your job history
                </h5>
              </div>
            </div>
            <div
              className='ob-page__section'
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <TableHistoryAg
                title='Analysis'
                onViewDetails={handleViewDetails}
                onViewLogs={handleViewLogs}
                onViewMessages={handleViewMessages}
              />
            </div>
          </div>

          {/* Right column: stacked panels in open order */}
          {hasRightColumn && (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                minWidth: 0,
                gap: 'var(--ob-spacing-4, 1rem)',
              }}
            >
              {panelOrder.map((panel, index) => {
                if (panel === 'details' && detailsJob) {
                  return (
                    <div
                      key='details'
                      ref={detailsPanelRef}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                      }}
                    >
                      <PanelTitle
                        title='Details'
                        subtitle='View job parameters and configuration'
                        isFirst={index === 0}
                      />
                      <div
                        className='ob-page__section'
                        style={{
                          position: 'relative',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <PanelCloseButton label='details' onClose={handleCloseDetails} />
                        <JobDetailsPanel job={detailsJob} onClose={handleCloseDetails} />
                      </div>
                    </div>
                  )
                }
                if (panel === 'logs' && logsJob) {
                  return (
                    <div
                      key='logs'
                      ref={logsPanelRef}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                      }}
                    >
                      <PanelTitle
                        title='Logs'
                        subtitle='View session logs and messages'
                        isFirst={index === 0}
                      />
                      <div
                        className='ob-page__section'
                        style={{
                          position: 'relative',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <PanelCloseButton label='logs' onClose={handleCloseLogs} />
                        <JobLogsPanel
                          job={logsJob}
                          tabType={logsTabType}
                          onClose={handleCloseLogs}
                        />
                      </div>
                    </div>
                  )
                }
                return null
              })}
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
}

export {JobViewerPage}
